import React, { useEffect, useRef, useState } from 'react';
import './ThemePicker.css';

/* Portal theme picker (ADDITIVE, portal-scoped).
   Self-contained disclosure menu — no new framework dependency. Icons use the
   FontAwesome Pro kit web-font (fa-sharp fa-solid …), matching the rest of the
   portal (PortalLayout, 300+ icons). The repo loads the Pro kit, not the
   svg-core CSS that <FontAwesomeIcon> needs, so kit classes are the working
   convention here.
   Adding a [data-theme] option later is a data change: append to a group in
   THEME_GROUPS here, to VALID in usePortalTheme.js, and a token block in tokens/colors.css. */
// Two groups (2026-10-30). Light / Dark / Match device are the main choice;
// the Comfort themes are the low-glare extras. Every hint DESCRIBES its theme
// and never its state: "Current look" sat under Default whichever theme was
// chosen, so it read as wrong on every other selection, and the check mark
// already says which one is on. Families never see the Stage, so no hint may
// lean on it.
const THEME_GROUPS = [
	{
		title: 'Theme',
		options: [
			{
				id: 'default',
				label: 'Light',
				hint: 'Bright and clear',
				icon: 'fa-sun',
			},
			{
				id: 'navy',
				label: 'Dark',
				hint: 'Deep navy, easy at night',
				icon: 'fa-moon',
			},
			{
				id: 'system',
				label: 'Match device',
				hint: 'Switches with your device',
				icon: 'fa-circle-half-stroke',
			},
		],
	},
	{
		title: 'Comfort, low glare',
		options: [
			{
				id: 'comfort',
				label: 'Comfort',
				hint: 'Warm paper',
				icon: 'fa-mug-hot',
			},
			{
				id: 'comfort-dark',
				label: 'Comfort Dark',
				hint: 'Warm charcoal',
				icon: 'fa-moon-cloud',
			},
			{
				id: 'comfort-system',
				label: 'Comfort, match device',
				hint: 'Switches with your device',
				icon: 'fa-circle-half-stroke',
			},
		],
	},
];
export const THEME_OPTIONS = THEME_GROUPS.flatMap((g) => g.options);

function ThemePicker({ value, onChange }) {
	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;
		const onDocPointer = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		const onKey = (e) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('mousedown', onDocPointer);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onDocPointer);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	const current = THEME_OPTIONS.find((o) => o.id === value) || THEME_OPTIONS[0];

	const handleSelect = (id) => {
		onChange(id);
		setOpen(false);
	};

	return (
		<div className='theme-picker' ref={rootRef}>
			<button
				type='button'
				className='theme-picker__toggle'
				aria-haspopup='menu'
				aria-expanded={open}
				aria-label={`Portal theme: ${current.label}`}
				title={`Portal theme: ${current.label}`}
				onClick={() => setOpen((v) => !v)}>
				<i className='fa-sharp fa-solid fa-palette' />
			</button>

			{open && (
				<div className='theme-picker__menu' role='menu'>
					{THEME_GROUPS.map((group) => (
						<div
							key={group.title}
							className='theme-picker__group'
							role='group'
							aria-label={group.title}>
							<div className='theme-picker__menu-title'>{group.title}</div>
							{group.options.map((opt) => {
								const active = opt.id === value;
								return (
									<button
										key={opt.id}
										type='button'
										role='menuitemradio'
										aria-checked={active}
										className={`theme-picker__item${
											active ? ' is-active' : ''
										}`}
										onClick={() => handleSelect(opt.id)}>
										<i
											className={`fa-sharp fa-solid ${opt.icon} fa-fw theme-picker__item-icon`}
										/>
										<span className='theme-picker__item-text'>
											<span className='theme-picker__item-label'>
												{opt.label}
											</span>
											<span className='theme-picker__item-hint'>
												{opt.hint}
											</span>
										</span>
										{active && (
											<i className='fa-sharp fa-solid fa-check theme-picker__item-check' />
										)}
									</button>
								);
							})}
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export default ThemePicker;
